import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import {
  MapPin, Plus, Star, Trash2, Edit3, X, Loader2, AlertTriangle,
  Check, Calendar, Home,
} from 'lucide-react';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC','PR','VI','GU','AS','MP','AA','AE','AP'];

const MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];

const mmddToLong = (mmdd) => {
  if (!mmdd) return '';
  const [m, d] = mmdd.split('-').map(Number);
  return `${MONTH_NAMES[m - 1]} ${d}`;
};


// ────────────────────────────────────────────────────────────────
// Add / Edit modal
// ────────────────────────────────────────────────────────────────
function AddressFormModal({ open, initial, onClose, onSaved }) {
  const [label, setLabel]     = useState('');
  const [street, setStreet]   = useState('');
  const [city, setCity]       = useState('');
  const [state, setState]     = useState('');
  const [zip, setZip]         = useState('');
  const [isYearRound, setIsYearRound] = useState(false);
  const [startMo, setStartMo] = useState('05');
  const [startDy, setStartDy] = useState('01');
  const [endMo, setEndMo]     = useState('09');
  const [endDy, setEndDy]     = useState('30');

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!initial) {
      setLabel(''); setStreet(''); setCity(''); setState(''); setZip('');
      setIsYearRound(false);
      setStartMo('05'); setStartDy('01'); setEndMo('09'); setEndDy('30');
    } else {
      setLabel(initial.label || '');
      setStreet(initial.street || '');
      setCity(initial.city || '');
      setState(initial.state || '');
      setZip(initial.zip || '');
      setIsYearRound(!!initial.isYearRound);
      if (initial.startDate) {
        setStartMo(initial.startDate.slice(0, 2));
        setStartDy(initial.startDate.slice(3));
      }
      if (initial.endDate) {
        setEndMo(initial.endDate.slice(0, 2));
        setEndDy(initial.endDate.slice(3));
      }
    }
    setErr('');
  }, [initial, open]);

  if (!open) return null;

  const handleSave = async () => {
    setErr('');
    if (!label.trim() || !street.trim() || !city.trim() || !state || !zip.trim()) {
      setErr('All fields are required.'); return;
    }
    const body = {
      label: label.trim(),
      street: street.trim(),
      city: city.trim(),
      state,
      zip: zip.trim(),
      isYearRound,
      startDate: isYearRound ? null : `${startMo}-${startDy}`,
      endDate:   isYearRound ? null : `${endMo}-${endDy}`,
    };
    setSaving(true);
    try {
      if (initial?.id) {
        await api.put(`/participant/addresses/${initial.id}`, body);
      } else {
        await api.post('/participant/addresses', body);
      }
      onSaved?.();
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(Array.isArray(detail) ? detail[0]?.msg || 'Save failed.' : detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const input = 'w-full h-10 px-3 bg-white border border-[#D1D5DB] rounded text-sm text-[#1B2A4A] focus:outline-none focus:border-[#1B2A4A] focus:ring-1 focus:ring-[#1B2A4A]/20';
  const select = input + ' appearance-none bg-[url("data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22%236B7280%22%3E%3Cpath%20d%3D%22M5%208l5%205%205-5H5z%22/%3E%3C/svg%3E")] bg-no-repeat bg-[right_0.5rem_center] pr-8';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="address-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E5E9]">
          <h3 className="text-sm font-semibold text-[#1B2A4A]">
            {initial ? 'Edit Address' : 'Add Mailing Address'}
          </h3>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#374151]" data-testid="close-address-modal">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {err && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="address-form-error">
              <AlertTriangle size={13} className="text-red-600" />
              <span className="text-xs text-red-700">{err}</span>
            </div>
          )}

          <div>
            <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Label</label>
            <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Primary Residence"
                   className={input} data-testid="input-address-label" maxLength={60} />
          </div>
          <div>
            <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Street Address</label>
            <input value={street} onChange={e => setStreet(e.target.value)} placeholder="1500 Pennsylvania Ave NW"
                   className={input} data-testid="input-address-street" maxLength={200} />
          </div>
          <div className="grid grid-cols-6 gap-2">
            <div className="col-span-3">
              <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">City</label>
              <input value={city} onChange={e => setCity(e.target.value)} placeholder="Washington"
                     className={input} data-testid="input-address-city" maxLength={80} />
            </div>
            <div className="col-span-1">
              <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">State</label>
              <select value={state} onChange={e => setState(e.target.value)} className={select} data-testid="input-address-state">
                <option value="">—</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">ZIP</label>
              <input value={zip} onChange={e => setZip(e.target.value)} placeholder="20220"
                     className={input + ' font-mono'} data-testid="input-address-zip" maxLength={10} />
            </div>
          </div>

          {/* Year-round toggle */}
          <div className="border-t border-[#E2E5E9] pt-3">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input type="checkbox" checked={isYearRound}
                     onChange={e => setIsYearRound(e.target.checked)}
                     className="h-4 w-4 text-[#1B2A4A] border-gray-300 rounded focus:ring-[#1B2A4A]"
                     data-testid="input-address-yearround" />
              <div>
                <span className="text-sm font-medium text-[#1B2A4A]">Year-round primary address</span>
                <p className="text-[11px] text-[#6B7280]">
                  Used whenever no seasonal address applies. Only one permitted.
                </p>
              </div>
            </label>
          </div>

          {/* Date range */}
          {!isYearRound && (
            <div data-testid="date-range-section" className="border rounded p-3 bg-[#F8F9FA] border-[#E2E5E9]">
              <div className="text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-2">Active Month/Day Range</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-[10px] text-[#9CA3AF] block mb-1">Start</span>
                  <div className="flex gap-1.5">
                    <select value={startMo} onChange={e => setStartMo(e.target.value)}
                            className={select + ' flex-1'} data-testid="input-start-month">
                      {MONTH_NAMES.map((m, i) => (
                        <option key={i} value={String(i + 1).padStart(2, '0')}>{m}</option>
                      ))}
                    </select>
                    <select value={startDy} onChange={e => setStartDy(e.target.value)}
                            className={select + ' w-20'} data-testid="input-start-day">
                      {Array.from({length: 31}, (_, i) => String(i + 1).padStart(2, '0')).map(d =>
                        <option key={d} value={d}>{parseInt(d)}</option>
                      )}
                    </select>
                  </div>
                </div>
                <div>
                  <span className="text-[10px] text-[#9CA3AF] block mb-1">End</span>
                  <div className="flex gap-1.5">
                    <select value={endMo} onChange={e => setEndMo(e.target.value)}
                            className={select + ' flex-1'} data-testid="input-end-month">
                      {MONTH_NAMES.map((m, i) => (
                        <option key={i} value={String(i + 1).padStart(2, '0')}>{m}</option>
                      ))}
                    </select>
                    <select value={endDy} onChange={e => setEndDy(e.target.value)}
                            className={select + ' w-20'} data-testid="input-end-day">
                      {Array.from({length: 31}, (_, i) => String(i + 1).padStart(2, '0')).map(d =>
                        <option key={d} value={d}>{parseInt(d)}</option>
                      )}
                    </select>
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-[#6B7280] mt-2">
                Ranges may wrap year-end (e.g. November 15 → April 20 for winter addresses).
              </p>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button onClick={onClose} disabled={saving}
                    className="flex-1 h-10 rounded text-xs font-medium text-[#6B7280] border border-[#D1D5DB] hover:bg-gray-50 disabled:opacity-50">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
                    data-testid="submit-address-form"
                    className="flex-1 h-10 rounded text-xs font-medium text-white bg-[#1B2A4A] hover:bg-[#0F1A33] disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {saving && <Loader2 size={12} className="animate-spin" />}
              {initial ? 'Save Changes' : 'Add Address'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// Main panel
// ────────────────────────────────────────────────────────────────
export const AddressesPanel = () => {
  const [addrs, setAddrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/participant/addresses');
      setAddrs(res.data || []);
      setErr('');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Unable to load addresses.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditTarget(null); setModalOpen(true); };
  const openEdit = (a) => { setEditTarget(a); setModalOpen(true); };

  const handleDelete = async (a) => {
    if (!window.confirm(`Remove "${a.label}"?`)) return;
    setBusyId(a.id); setErr('');
    try {
      await api.delete(`/participant/addresses/${a.id}`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Unable to remove address.');
    } finally {
      setBusyId(null);
    }
  };

  const handleSetYearRound = async (a) => {
    setBusyId(a.id); setErr('');
    try {
      await api.put(`/participant/addresses/${a.id}`, {
        label: a.label, street: a.street, city: a.city, state: a.state, zip: a.zip,
        isYearRound: true, startDate: null, endDate: null,
      });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Unable to set year-round primary.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="panel p-0 overflow-hidden" data-testid="addresses-panel">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E5E9] bg-[#FAFBFC]">
        <div>
          <h2 className="text-sm font-semibold text-[#1B2A4A] uppercase tracking-wider">
            Mailing Addresses
          </h2>
          <p className="text-[11px] text-[#6B7280] mt-0.5">
            Add seasonal or year-round addresses. The system routes mail to the address active today.
          </p>
        </div>
        <button onClick={openAdd} data-testid="add-address-btn"
                className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-xs font-medium bg-[#1B2A4A] text-white hover:bg-[#0F1A33] transition-colors">
          <Plus size={13} /> Add Address
        </button>
      </div>

      {err && (
        <div className="px-5 py-3 bg-red-50 border-b border-red-200" data-testid="addresses-error">
          <p className="text-xs text-red-700">{err}</p>
        </div>
      )}

      <div className="divide-y divide-[#F0F1F3]">
        {loading ? (
          <div className="p-6 text-center text-xs text-[#9CA3AF]">Loading…</div>
        ) : addrs.length === 0 ? (
          <div className="p-8 text-center" data-testid="no-addresses">
            <MapPin size={28} className="mx-auto text-[#D1D5DB] mb-2" />
            <p className="text-sm text-[#6B7280]">No addresses on file yet.</p>
          </div>
        ) : (
          addrs.map(a => (
            <AddressRow
              key={a.id}
              a={a}
              busy={busyId === a.id}
              canDelete={addrs.length > 1}
              onEdit={() => openEdit(a)}
              onDelete={() => handleDelete(a)}
              onSetYearRound={() => handleSetYearRound(a)}
            />
          ))
        )}
      </div>

      <AddressFormModal
        open={modalOpen}
        initial={editTarget}
        onClose={() => setModalOpen(false)}
        onSaved={load}
      />
    </div>
  );
};


function AddressRow({ a, busy, canDelete, onEdit, onDelete, onSetYearRound }) {
  const activeRange = a.isYearRound
    ? 'Year-round primary'
    : a.startDate && a.endDate
      ? `Active: ${mmddToLong(a.startDate)} through ${mmddToLong(a.endDate)}`
      : '—';

  return (
    <div
      className={
        'px-5 py-4 flex items-start gap-4 flex-wrap ' +
        (a.isCurrent ? 'bg-emerald-50/40' : '')
      }
      data-testid={`address-row-${a.id}`}
    >
      <div className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center border"
           style={{ backgroundColor: a.isYearRound ? '#EEF2FF' : '#FFFBEB',
                    borderColor:   a.isYearRound ? '#C7D2FE' : '#FDE68A' }}>
        {a.isYearRound ? <Home size={14} className="text-[#1B2A4A]" /> : <Calendar size={14} className="text-[#B45309]" />}
      </div>

      <div className="flex-1 min-w-[260px]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-[#1B2A4A]" data-testid={`address-label-${a.id}`}>
            {a.label}
          </span>
          {a.isCurrent && (
            <span className="inline-flex items-center gap-1 text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-300"
                  data-testid={`address-current-badge-${a.id}`}>
              <Check size={9} /> CURRENTLY ACTIVE
            </span>
          )}
          {a.isYearRound && (
            <span className="inline-flex items-center gap-1 text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-[#1B2A4A] text-white"
                  data-testid={`address-yearround-badge-${a.id}`}>
              <Star size={9} /> YEAR-ROUND
            </span>
          )}
        </div>
        <p className="text-xs text-[#374151] mt-1" data-testid={`address-street-${a.id}`}>
          {a.street}
        </p>
        <p className="text-xs text-[#6B7280]" data-testid={`address-csz-${a.id}`}>
          {a.city}, {a.state} {a.zip}
        </p>
        <p className="text-[10px] font-medium mt-1.5"
           style={{ color: a.isYearRound ? '#1B2A4A' : '#B45309' }}
           data-testid={`address-range-${a.id}`}>
          {activeRange}
        </p>
      </div>

      <div className="flex items-center gap-1.5 shrink-0">
        {!a.isYearRound && (
          <button onClick={onSetYearRound} disabled={busy}
                  title="Set as year-round primary"
                  data-testid={`set-yearround-${a.id}`}
                  className="h-7 px-2.5 rounded text-[10px] font-medium text-[#1B2A4A] border border-[#D1D5DB] hover:border-[#1B2A4A] hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-1">
            <Star size={10} /> Set Year-Round
          </button>
        )}
        <button onClick={onEdit} disabled={busy}
                title="Edit"
                data-testid={`edit-address-${a.id}`}
                className="h-7 w-7 rounded text-[#6B7280] hover:text-[#1B2A4A] hover:bg-gray-50 border border-transparent hover:border-[#D1D5DB] inline-flex items-center justify-center disabled:opacity-50">
          <Edit3 size={12} />
        </button>
        <button onClick={onDelete} disabled={busy || !canDelete}
                title={canDelete ? 'Remove' : 'Cannot remove only address on file'}
                data-testid={`delete-address-${a.id}`}
                className="h-7 w-7 rounded text-[#9CA3AF] hover:text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 inline-flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
        </button>
      </div>
    </div>
  );
}

export default AddressesPanel;
